import React, { forwardRef } from 'react';
import type { TableCellProps as MuiTableCellProps } from '@mui/material';
import { TableCell as MuiTableCell } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

import type { TooltipInjectedProps } from '../Tooltip/tooltipInjectedProps';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!
**/

/**
 * Explicit TableCell props based on actual usage in the codebase.
 *
 * Extends TooltipInjectedProps so wrapping a TableCell in a MUI <Tooltip> (which
 * clones its child to inject hover/focus handlers, the native title and
 * aria-describedby) does not trip the unexpected-props guard below.
 */
export interface TableCellProps extends TooltipInjectedProps<HTMLTableCellElement> {
  /** CSS class name to apply to the root element */
  className?: string;
  /** Inline styles applied to the root element */
  style?: React.CSSProperties;
  /** Set the text-align on the table cell content */
  align?: MuiTableCellProps['align'];
  /**
   * Sets the padding applied to the cell. `'default'` was v4's name for what v5 calls `'normal'`;
   * it is kept as an alias and remapped below, so the 35 call sites that pass it need no edit.
   */
  padding?: MuiTableCellProps['padding'] | 'default';
  /** Set aria-sort direction */
  sortDirection?: MuiTableCellProps['sortDirection'];
  /** The component used for the root node */
  component?: MuiTableCellProps['component'];
  /** Set the scope attribute */
  scope?: MuiTableCellProps['scope'];
  /** Number of columns a cell should span */
  colSpan?: number;
  /** Number of rows a cell should span */
  rowSpan?: number;
  /** Callback fired when the cell is clicked */
  onClick?: React.MouseEventHandler<HTMLTableCellElement>;
  /** The content of the table cell */
  children?: React.ReactNode;
}

// ref is forwarded so parents that need the DOM node (e.g. MUI <Tooltip>, which
// anchors its popper to the cloned child) keep working through the wrapper.
const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  (
    {
      className,
      style,
      align,
      padding,
      sortDirection,
      component,
      scope,
      colSpan,
      rowSpan,
      onClick,
      children,
      title,
      'aria-describedby': ariaDescribedBy,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'data-mui-internal-clone-element': dataMuiInternalCloneElement,
      onMouseOver,
      onMouseLeave,
      onFocus,
      onBlur,
      onTouchStart,
      onTouchEnd,
      ...restProps
    },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to TableCell: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the TableCellProps interface and to the component's signature.`,
        true
      );
    }
    return (
      <MuiTableCell
        ref={ref}
        className={className}
        style={style}
        align={align}
        padding={padding === 'default' ? 'normal' : padding}
        sortDirection={sortDirection}
        component={component}
        scope={scope}
        colSpan={colSpan}
        rowSpan={rowSpan}
        onClick={onClick}
        title={title}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        data-mui-internal-clone-element={dataMuiInternalCloneElement}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        {...restProps}
      >
        {children}
      </MuiTableCell>
    );
  }
);

TableCell.displayName = 'TableCell';

export default TableCell;
